import React, { useRef } from 'react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { getFileIcon, formatFileSize } from '../../lib/libraryUtils';

/**
 * Upload panel with dropzone and file list
 */
const UploadPanel = ({
  files,
  isUploading,
  pendingCount,
  onFilesSelected,
  onRemoveFile,
  onStartUpload,
  onClearFiles,
}) => {
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles?.length > 0) {
      onFilesSelected(droppedFiles);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles?.length > 0) {
      onFilesSelected(selectedFiles);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="upload-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="text-4xl mb-2">📁</div>
        <p className="text-gray-600 mb-1">Sleep bestanden hierheen</p>
        <p className="text-sm text-gray-400">of klik om te selecteren</p>
        <p className="text-xs text-gray-400 mt-2">
          Max 10MB per bestand (PDF, afbeeldingen tot 50MB met compressie)
        </p>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm text-gray-700">
              Geselecteerde bestanden ({files.length})
            </h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClearFiles}
              disabled={isUploading}
              className="text-gray-500"
            >
              Alles wissen
            </Button>
          </div>

          <div className="max-h-60 overflow-y-auto space-y-2">
            {files.map((fileObj, index) => (
              <UploadFileRow
                key={`${fileObj.name}-${index}`}
                fileObj={fileObj}
                index={index}
                onRemove={() => onRemoveFile(index)}
                isUploading={isUploading}
              />
            ))}
          </div>

          {/* Upload button */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              onClick={onStartUpload}
              disabled={isUploading || pendingCount === 0}
              data-testid="btn-start-upload"
            >
              {isUploading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Uploading...
                </>
              ) : (
                `Upload ${pendingCount} bestand${pendingCount !== 1 ? 'en' : ''}`
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const UploadFileRow = ({ fileObj, index, onRemove, isUploading }) => {
  const { name, sizeFormatted, extension, status, progress, statusText, error, needsCompression } = fileObj;

  const getStatusColor = () => {
    switch (status) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'error': return 'bg-red-50 border-red-200';
      case 'uploading': return 'bg-blue-50 border-blue-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div 
      className={`p-3 rounded-lg border ${getStatusColor()} transition-colors`}
      data-testid={`upload-file-${index}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="text-xl">{getFileIcon(extension)}</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{name}</p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{sizeFormatted}</span>
              {needsCompression && status === 'pending' && (
                <span className="text-orange-600">Wordt gecomprimeerd</span>
              )}
              {statusText && (
                <span className={status === 'success' ? 'text-green-600' : 'text-blue-600'}>
                  {statusText}
                </span>
              )}
              {error && (
                <span className="text-red-600">{error}</span>
              )}
            </div>
          </div>
        </div>

        {/* Status indicator / Remove button */}
        <div className="flex items-center gap-2 ml-2">
          {status === 'success' && <span className="text-green-600">✓</span>}
          {status === 'error' && <span className="text-red-600">✕</span>}
          {status === 'pending' && !isUploading && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRemove}
              className="text-gray-400 hover:text-red-600"
            >
              ✕
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {status === 'uploading' && (
        <div className="mt-2">
          <Progress value={progress} className="h-1.5" />
        </div>
      )}
    </div>
  );
};

export default UploadPanel;
